import React, { useRef, useState } from "react";
import classNames from "classnames";
import CustomLink from "../../atoms/CustomLink/CustomLink.atom";
import Button from "../../atoms/Button/Button.component";
import Close from "../../../assets/svgs/Close.svg";
import ContactCard, { ContactCardProps } from "../ContactCardSection/ContactCard.blok";
import { ButtonProps } from "../../../interfaces/Button.interface";
import { ButtonTypes } from "../../../interfaces/enums/ButtonTypes.enum";
import { ButtonSizeTypes } from "../../../interfaces/enums/ButtonSizeTypes.enum";
import {
  StickyButtonIconPosition,
  StickyButtonPosition,
  StoryblokStickyButton,
} from "../../../interfaces/Storyblok/StoryblokStickyButton.interface";
import getCurrentUserState from "../../../utils/getCurrentUserState/getCurrentUserState.util";
import matchLocationToState, {
  AustralianStateCode,
} from "../../../utils/getCurrentUserState/matchLocationToState.util";
import { EnquiryIcon, getIconUrl, positionConfig, usePanelAnimation } from "./stickyPanel.shared";

/*-- Falls back to the first card whenever the state is unknown or nothing matches it. --*/
const getCardForState = (
  contactCards: ContactCardProps[],
  state: AustralianStateCode | undefined,
): ContactCardProps | undefined => {
  const matchedCard = state
    ? contactCards.find((card: ContactCardProps) => matchLocationToState(card.location, state))
    : undefined;

  return matchedCard ?? contactCards[0];
};

const hasLabel = (buttons: ButtonProps[]): boolean =>
  Array.isArray(buttons) && buttons.length > 0 && !!buttons[0].label;

const StickyButton: React.FC<StoryblokStickyButton> = ({
  sticky_button,
  position,
  override_click,
  contact_cards = [],
  custom_icon,
  icon_position,
  custom_color,
  contact_card_redirect_link,
  contact_card_link_label,
}) => {
  const { isPanelOpen, isPanelMounted, isPanelVisible, openPanel: openPanelBase, closePanel, closeButtonRef } =
    usePanelAnimation();
  const [isLocating, setIsLocating] = useState<boolean>(false);
  const [userState, setUserState] = useState<AustralianStateCode | undefined>(undefined);
  // Remembers that detection has already run so re-opening the panel never re-prompts.
  const hasResolvedStateRef = useRef<boolean>(false);

  if (!hasLabel(sticky_button)) {
    return null;
  }

  const button = sticky_button[0];
  const selectedPosition: StickyButtonPosition = (Array.isArray(position) ? position[0] : position) ?? "bottom-right";
  const config = positionConfig[selectedPosition] ?? positionConfig["bottom-right"];

  const cachedUrl = typeof button.link === "string" ? button.link : button.link?.cached_url;
  const anchor = typeof button.link === "object" ? button.link?.anchor : undefined;
  const linkType = typeof button.link === "object" ? button.link?.linktype : undefined;

  const customColor = custom_color?.trim() ? custom_color.trim() : "#ED3E42";

  const isOverridden = !!override_click && contact_cards.length > 0;

  // Resolved the same way as the tab's own link so the CTA handles internal paths,
  // same-page anchors and outbound URLs identically.
  const redirectUrl = (
    typeof contact_card_redirect_link === "string" ? contact_card_redirect_link : contact_card_redirect_link?.cached_url
  )?.trim();
  const redirectLabel = contact_card_link_label?.trim();

  if (!isOverridden && !cachedUrl) {
    return null;
  }

  const tabClassName = classNames(
    "group flex flex-col items-center gap-[10px] px-[9px] py-[21px] text-White shadow-2xl transition-all duration-300 ease-in-out hover:brightness-95 focus:outline-none focus:ring-0",
    config.rounded,
  );

  // Applied inline because Tailwind can only generate arbitrary values it sees at build time,
  // so an authored colour can never come through as a bg-[...] class.
  const tabStyle: React.CSSProperties = { backgroundColor: customColor };

  const customIconUrl = getIconUrl(custom_icon);
  const selectedIconPosition: StickyButtonIconPosition =
    (Array.isArray(icon_position) ? icon_position[0] : icon_position) ?? "leading";

  const labelContent = (
    <span
      className="font-extrabold uppercase tracking-[0.25em] text-sm leading-none"
      style={{
        writingMode: "vertical-rl",
        transform: config.readUpwards ? "rotate(180deg)" : undefined,
      }}
    >
      {button.label}
    </span>
  );

  const iconContent = customIconUrl ? (
    <img
      src={customIconUrl}
      // Decorative: the tab already takes its accessible name from the visible label.
      alt=""
      aria-hidden="true"
      // vertical-rl lays the label on its side and readUpwards adds the 180deg that makes it read
      // bottom-to-top. Matching both angles lets an upright icon lie the same way as the letters,
      // where the old flat 180deg left it standing on its head.
      style={{ transform: `rotate(${config.readUpwards ? 270 : 90}deg)` }}
      // object-contain keeps a non-square PNG or JPEG in proportion instead of stretching it.
      className="h-6 w-6 shrink-0 object-contain"
    />
  ) : (
    <EnquiryIcon flip={!config.readUpwards} />
  );

  // The column stacks top-to-bottom, but the label reads bottom-to-top on right-hand tabs, so there
  // the leading icon has to come last in the DOM to end up ahead of the words.
  const isIconFirst = config.readUpwards ? selectedIconPosition === "trailing" : selectedIconPosition === "leading";

  const tabContent = (
    <>
      {isIconFirst && iconContent}
      {labelContent}
      {!isIconFirst && iconContent}
    </>
  );

  const openPanel = () => {
    openPanelBase();

    if (hasResolvedStateRef.current) {
      return;
    }

    setIsLocating(true);
    getCurrentUserState().then((state: AustralianStateCode | undefined) => {
      hasResolvedStateRef.current = true;
      setUserState(state);
      setIsLocating(false);
    });
  };

  const activeCard = getCardForState(contact_cards, userState);

  return (
    <>
      <div className={classNames("fixed z-50", config.container)}>
        {isOverridden ? (
          <button
            type="button"
            onClick={openPanel}
            className={tabClassName}
            style={tabStyle}
            aria-expanded={isPanelOpen}
          >
            {tabContent}
          </button>
        ) : (
          <CustomLink
            url={`${cachedUrl ?? ""}`}
            anchor={typeof anchor === "string" ? anchor : ""}
            linkType={linkType}
            className={tabClassName}
            style={tabStyle}
          >
            {tabContent}
          </CustomLink>
        )}
      </div>

      {isOverridden && isPanelMounted && (
        <div
          className={classNames(
            "fixed inset-0 z-[60] flex items-center justify-center overflow-hidden bg-Opacity5 p-4 transition-opacity duration-300 ease-out motion-reduce:transition-none sm:block sm:p-0",
            // Stops the backdrop swallowing clicks during the 300ms slide-out.
            isPanelVisible ? "opacity-100" : "pointer-events-none opacity-0",
          )}
          onClick={closePanel}
          role="presentation"
        >
          <div
            className={classNames(
              "relative w-full max-w-[402.5px] transition-transform duration-300 ease-out motion-reduce:transition-none sm:absolute",
              config.panel,
              isPanelVisible ? "translate-x-0" : config.panelClosed,
            )}
            onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={button.label}
          >
            <button
              ref={closeButtonRef}
              type="button"
              onClick={closePanel}
              aria-label="Close"
              className="absolute -top-11 right-0 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-White text-Primary-900 shadow-lg transition-transform duration-300 hover:scale-110"
            >
              <Close className="h-3.5 w-3.5" />
            </button>

            {isLocating ? (
              <div className="flex w-full items-center gap-4 rounded-[24px] bg-White p-6 shadow-lg">
                <span
                  aria-hidden="true"
                  className="h-6 w-6 shrink-0 animate-spin rounded-full border-2 border-Primary-200 border-t-SP6"
                />
                <p className="text-[15px] font-normal leading-[20px] text-Body">Finding your closest contact&hellip;</p>
              </div>
            ) : (
              !!activeCard && (
                <ul className="w-full">
                  <ContactCard key={activeCard._uid} {...activeCard} />
                </ul>
              )
            )}

            {!isLocating && !!redirectUrl && !!redirectLabel && (
              <div className="mt-6 flex w-full justify-center">
                <Button
                  link={contact_card_redirect_link}
                  label={redirectLabel}
                  type={ButtonTypes.PRIMARY}
                  size={ButtonSizeTypes.LG}
                  // Closing first releases the scroll lock, so a same-page anchor can actually scroll to its target.
                  onClick={closePanel}
                  fullWidth
                  wrap
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default StickyButton;
